import React, { useEffect, useRef, useState } from "react";
import { Loader, MonitorCheck, X } from "lucide-react";
import DatePicker from "react-datepicker";
import { can_visit, formatDate, notify } from "../../helpers";
import Spacing from "../Spacing";
import { Drawer } from 'vaul';
import { checkClaim } from "../../services/npiManagement";

export default function ClaimSearchModal({
  account_id,
  location_id,
  activeModal = false,
  setActiveModal = () => {},
  previousScrollPosition = 0,
  setClaimSearchData = () => {},
  patientData = {},
  handleCheckClaimRow = () => {},
}) {
  const dropdownRef = useRef(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [checkedClaimLoader, setCheckedClaimLoader] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let el = document.getElementById("docpro-datepicker-portal");
    if (!el) {
      el = document.createElement("div");
      el.id = "docpro-datepicker-portal";
      document.body.appendChild(el);
    }
  }, []);

  const handleConfirmAppointment = async() => {
    const postData = {
      account_id: account_id,
      location_id: location_id,
      patient_id: patientData?.id,
      waiting_room_id: null,
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    };
    if(can_visit("eligibility")){
      setCheckedClaimLoader(true);
      try {
        const response = await checkClaim(postData);
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success"){
              if(response?.data?.all_dos?.length === 0 && (!response?.data?.result || Object.keys(response?.data?.result).length === 0) && (!response?.data?.raw_request || Object.keys(response?.data?.raw_request)?.length === 0)) {
                notify("error", response?.data?.message, 5);
                return;
              }
              setClaimSearchData(response?.data);
              handleDrawerClose();
              handleCheckClaimRow();
            } else {
              if(response?.data?.raw_request || Object.keys(response?.data?.raw_request)?.length > 0) {
                setClaimSearchData(response?.data);
                handleDrawerClose();
                handleCheckClaimRow();
              } else {
                notify("error", response?.data?.message, 5);
              }
            }
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail);
        }
      } finally {
        setCheckedClaimLoader(false);
      }
    }
  };

  const isValidDate = (d) => d instanceof Date && !isNaN(d);

  const parseMMDDYYYY = (str) => {
    if (typeof str !== "string") return null;
    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const mm = Number(m[1]);
    const dd = Number(m[2]);
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, mm - 1, dd);
    // ensure no overflow (e.g., 02/31/2024)
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return d;
  };

  const isDisabled = checkedClaimLoader || (!startDate && !endDate);

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose} dismissible={false}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 medium" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <Drawer.Title className="sr-only">Check Claim</Drawer.Title>
            <Drawer.Description className="sr-only">Claim Search For a Patient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_2 docpro_style_auto">
              <div className="docpro_modal">
                <div className="modal_height_100">
                  <div>
                    <button
                      className="docpro_close_btn"
                      onClick={handleDrawerClose}
                    >
                      <X />
                    </button>
                    <h2 className="docpro_fs_24 docpro_mb_0">Check Claim</h2>
                    <Spacing lg="20" sm="20" />
                    <label className="docpro_fw_500 docpro_primary_color">
                      Beginning date of service
                    </label>
                    <DatePicker
                      selected={startDate}
                      onChange={(d) => setStartDate(d)}
                      onSelect={(d) => {
                        if (!endDate && isValidDate(d)) setEndDate(d);
                      }}
                      onBlur={(e) => {
                        if (!endDate) {
                          const parsed = parseMMDDYYYY(e.target.value);
                          if (parsed && isValidDate(parsed)) {
                            if (!startDate || +startDate !== +parsed) setStartDate(parsed);
                            setEndDate(parsed);
                          }
                        }
                      }}
                      className="size_lg"
                      placeholderText='mm/dd/yyyy'
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="docpro_datepicker_popper"
                    />
                    <Spacing lg="20" sm="20" />
                    <label className="docpro_fw_500 docpro_primary_color">
                      End date of service
                    </label>
                    <DatePicker
                      selected={endDate}
                      onChange={(d) => setEndDate(d)}
                      onSelect={(d) => {
                        if (!startDate && isValidDate(d)) setStartDate(d);
                      }}
                      onBlur={(e) => {
                        if (!startDate) {
                          const parsed = parseMMDDYYYY(e.target.value);
                          if (parsed && isValidDate(parsed)) {
                            if (!endDate || +endDate !== +parsed) setEndDate(parsed);
                            setStartDate(parsed);
                          }
                        }
                      }}
                      className="size_lg"
                      placeholderText='mm/dd/yyyy'
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="docpro_datepicker_popper"
                    />
                    <Spacing lg="20" sm="20" />
                  </div>
                  <button
                    className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn docpro_mt_auto"
                    style={{ width: "100%", padding: "10px 12px" }}
                    onClick={handleConfirmAppointment}
                    disabled = {isDisabled}
                  >
                    <MonitorCheck width={20} height={20} />
                    <span>Confirm Search</span>
                    {checkedClaimLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
